// Copyright (c) Docugami, Inc. All rights reserved.

import * as signalR from "@microsoft/signalr";
import { authAccessor } from "authentication/authAccessor";
import axios, { AxiosRequestConfig, Method } from "axios";
import { Config } from "config";
import { store } from "store";
import { addNetworkError, resetNetworkErrors } from "store/errorSlice";
import { refreshSignalRDependencies } from "store/reports/reportsSlice";
import { SignalRLogger } from "telemetry/SignalRLogger";
import { TelemetryExceptions } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { sleep } from "utils/AsyncHelpers";
import { Logger, LoggerCategory } from "utils/Logger";
import { isNetworkError } from "utils/errorUtils";

class AxiosSignalRHttpClient extends signalR.HttpClient {
    async send(request: signalR.HttpRequest): Promise<signalR.HttpResponse> {
        const config: AxiosRequestConfig = {
            url: request.url,
            data: request.content,
            headers: request.headers,
            method: request.method as Method
        };

        const response = await axios.request(config);
        return new signalR.HttpResponse(response.status, response.statusText, response.request?.response);
    }
}

class OrganizationSignalRConnection {
    private readonly _connection: signalR.HubConnection;
    private readonly _logger: SignalRLogger;
    private readonly _maxRetries = 60;
    private _retryCount: number = 0;
    private _stopped: boolean = false;

    get connection(): signalR.HubConnection { return this._connection; }

    constructor(organizationId: string) {
        this._logger = new SignalRLogger();

        const organizationSignalRUrl = `${Config.workspaceApiUrl}/hub`;
        this._connection = new signalR.HubConnectionBuilder()
            .withUrl(organizationSignalRUrl, {
                httpClient: new AxiosSignalRHttpClient(),
                headers: { 'x-docugami-organization-id': organizationId },
                accessTokenFactory: () => authAccessor.getAccessToken()
            })
            .configureLogging(this._logger)
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: context => {
                    if (isNetworkError(context.retryReason)) {
                        store.dispatch(addNetworkError());
                    }

                    return Math.pow(2, Math.min(context.previousRetryCount, 4));
                }
            })
            .build();

        this._connection.onclose(this.onClose);
        this._connection.onreconnected(() => {
            if (store.getState().errors.networkErrorCount > 0) {
                store.dispatch(refreshSignalRDependencies());
            }
            store.dispatch(resetNetworkErrors());
        });
    }

    async start() {
        this._stopped = false;

        let error: Error | undefined;
        let connected: boolean = false;
        while (!connected && this._retryCount < this._maxRetries) {
            try {
                await this._connection.start();
                await this.addToGroup();
                connected = true;
                Logger.LogInformation(LoggerCategory.SignalR, "SignalR connected");
            }
            catch (e) {
                error = e as Error;

                this._retryCount++;
                await sleep(5000);
            }
        }

        if (!connected) {
            Telemetry.trackException(error as Error, { name: TelemetryExceptions.SignalRConnectionFailed })
            throw error;
        }
    }

    async stop() {
        if (!this._stopped) {
            try {
                this._stopped = true;
                await this._connection.stop();
            }
            catch (error) {
            }
        }
    }

    private async addToGroup() {
        const organizationSignalRAddToGroupUrl = `${Config.workspaceApiUrl}/hub/add`;
        await axios.post(organizationSignalRAddToGroupUrl);
    }

    private onClose = (error?: Error) => {
        if (!this._stopped && !!error) {
            this._retryCount = 0;
            this.start();
        }
    }
}

export const waitForSignalRConnection = async (): Promise<OrganizationSignalRConnection> => {
    while (!OrganizationSignalRConnectionInstance) {
        await sleep(1000);
    }

    return OrganizationSignalRConnectionInstance!;
}

export let OrganizationSignalRConnectionInstance: OrganizationSignalRConnection | null = null;
export const createSignalRConnection = (organizationId: string): OrganizationSignalRConnection => {
    stopSignalRConnection();

    OrganizationSignalRConnectionInstance = new OrganizationSignalRConnection(organizationId);
    return OrganizationSignalRConnectionInstance;
}

export const stopSignalRConnection = () => {
    OrganizationSignalRConnectionInstance?.stop();
    OrganizationSignalRConnectionInstance = null;
}
